import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  Observable,
  Subject,
  switchMap,
} from 'rxjs';
import { catchError, filter, map, takeUntil, tap } from 'rxjs/operators';

import {
  PromotionClient,
  PromotionWithCentersResponse,
} from '@medindex-webapi';
import { HelixRoutes } from '@shared/enums/routes';
import { CityId } from '@shared/types/storage';
import { StorageService } from '@shared/core/services/storage.service';
import { BreadcrumbItem } from '@shared/modules/breadcrumb/types/breadcrumbItem';
import { RegionsService } from '@shared/services/regions.service';
import { PlatformService } from '@shared/services/platform.service';
import { MetaService } from '@shared/services/meta/meta.service';
import { YmService } from '@shared/services/yandex-metrica/ym.service';
import { YmGoalCatalogItem } from '../../../catalog/enums/ym-goal-catalog-item.enum';
import { PromotionStatus } from '../../enums/promotion-status.enum';
import { NavigationMenuService } from '@shared/services/navigation-menu.service';

@Component({
  selector: 'app-promotion',
  templateUrl: './promotion-page.component.html',
  styleUrls: ['./promotion-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RegionsService],
})
export class PromotionPageComponent implements OnInit, OnDestroy {
  @ViewChild('mapContainer') mapContainer: ElementRef<HTMLElement> | undefined;

  @HostListener('click', ['$event.target'])
  public onClick(element: HTMLElement): void {
    const url = (element as HTMLLinkElement)?.href?.toLowerCase();
    if (url && url.includes(HelixRoutes.CatalogItem)) {
      this.ymService.reachGoal(YmGoalCatalogItem.OpenFromPromotion);
    }
  }

  promotion$ = new BehaviorSubject<PromotionWithCentersResponse | null>(null);
  readonly PromotionStatus = PromotionStatus;
  private ngUnsubscribe$ = new Subject<void>();

  title$: Observable<string> = this.promotion$.pipe(
    filter(
      (
        promotion: PromotionWithCentersResponse | null
      ): promotion is PromotionWithCentersResponse => !!promotion
    ),
    map((promotion: PromotionWithCentersResponse) => promotion.title ?? '')
  );

  promotionsTitle$: Observable<string> =
    this.regions.getTitleWithCity('Скидки и акции');
  promotionsLink$: Observable<string> = this.regions.getLinkWithCity(
    `/${HelixRoutes.Promotions}`
  );

  breadcrumbs$: Observable<BreadcrumbItem[]> = combineLatest([
    this.title$,
    this.promotionsTitle$,
    this.promotionsLink$,
  ]).pipe(
    map(([title, promotionsTitle, promotionsLink]): BreadcrumbItem[] => {
      return [
        {
          text: 'Главная',
          href: `/`,
        },
        {
          text: promotionsTitle,
          href: promotionsLink,
        },
        {
          text: title,
          href: '',
        },
      ];
    })
  );

  mapTitle$ = this.navigationService.isHelixLiteAvailable$.pipe(
    map((isHelixLite) =>
      isHelixLite
        ? 'Пункты Helix Lite, в которых действует акция'
        : 'Центры Хеликс, в которых действует акция'
    )
  );

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private promotionClient: PromotionClient,
    private storage: StorageService,
    private platform: PlatformService,
    private regions: RegionsService,
    private metaService: MetaService,
    private ymService: YmService,
    private navigationService: NavigationMenuService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  scrollToMap() {
    this.mapContainer?.nativeElement.scrollIntoView({
      behavior: 'smooth',
    });
  }

  private loadData(): void {
    combineLatest([this.route.params, this.storage.cityId$])
      .pipe(
        this.regions.getWaitForAliasAppliedToUrlPipeOperator(),
        switchMap(([{ promotionId }, cityId]: [Params, CityId]) =>
          this.promotionClient.getById(promotionId, cityId as number)
        ),
        tap((promotion: PromotionWithCentersResponse): void => {
          this.metaService.title(promotion.title ?? '');
          this.promotion$.next(promotion);
        }),
        catchError(() => {
          if (this.platform.isBrowser) {
            this.router.navigateByUrl(`/${HelixRoutes.Error}`);
          }
          return EMPTY;
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
